import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxAction {
  label: string;
  onSelect: () => void;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value: string; // '' = nothing selected
  onChange: (value: string) => void;
  placeholder?: string;
  // Default true: sort alphabetically by label. Pass false to keep the
  // caller's order (e.g. an "All …" filter option pinned first).
  sort?: boolean;
  // Pinned rows at the bottom of the popup, always visible regardless of the
  // filter text (e.g. "＋ New label…").
  actions?: ComboboxAction[];
  disabled?: boolean;
  // Replaces the default trigger-button classes so each app can match its own
  // form-field idiom.
  buttonClassName?: string;
}

const DEFAULT_BUTTON_CLASS =
  'w-full flex items-center justify-between gap-2 px-2 py-1 text-sm text-left border border-gray-300 dark:border-neutral-700 rounded bg-white dark:bg-neutral-950 disabled:opacity-50';

// Estimated max popup height (filter input + list + actions) used to decide
// whether to open upward near the bottom of the viewport.
const POPUP_SPACE = 300;

interface PopupPos {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  sort = true,
  actions = [],
  disabled = false,
  buttonClassName,
}: ComboboxProps) {
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState<PopupPos | null>(null);

  const sorted = useMemo(
    () => (sort ? [...options].sort((a, b) => a.label.localeCompare(b.label)) : options),
    [options, sort]
  );

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter((o) => o.label.toLowerCase().includes(needle));
  }, [sorted, filter]);

  // Visible rows = filtered options followed by action rows; the highlight
  // index walks across both so ArrowDown reaches "＋ New …".
  const rowCount = filtered.length + actions.length;

  const selected = options.find((o) => o.value === value);

  const openPopup = () => {
    if (disabled) return;
    setFilter('');
    const selectedIdx = sorted.findIndex((o) => o.value === value);
    setHighlight(selectedIdx >= 0 ? selectedIdx : 0);
    setOpen(true);
  };

  const close = (refocusTrigger: boolean) => {
    setOpen(false);
    setFilter('');
    setPos(null);
    if (refocusTrigger) triggerRef.current?.focus();
  };

  const selectRow = (index: number) => {
    if (index < filtered.length) {
      onChange(filtered[index].value);
      close(true);
    } else {
      const action = actions[index - filtered.length];
      if (action) {
        action.onSelect();
        close(true);
      }
    }
  };

  // Position the portal popup from the trigger's viewport rect; flip upward
  // when there isn't room below. Track resize and any ancestor scroll.
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < POPUP_SPACE && rect.top > spaceBelow;
      setPos({
        left: rect.left,
        width: Math.max(rect.width, 220),
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  // Click-outside closes (without stealing focus back to the trigger).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popupRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close(false);
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Autofocus the filter input once the popup is positioned.
  useEffect(() => {
    if (open && pos) inputRef.current?.focus({ preventScroll: true });
  }, [open, pos]);

  // Keep the highlighted row in view.
  useEffect(() => {
    if (!open) return;
    document.getElementById(`${id}-row-${highlight}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, highlight, id]);

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, rowCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rowCount > 0) selectRow(Math.min(highlight, rowCount - 1));
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      close(true);
    }
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (!open && e.key === 'ArrowDown') {
      e.preventDefault();
      openPopup();
    }
  };

  const listboxId = `${id}-listbox`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        disabled={disabled}
        onClick={() => (open ? close(true) : openPopup())}
        onKeyDown={onTriggerKeyDown}
        className={buttonClassName || DEFAULT_BUTTON_CLASS}
      >
        <span className={`truncate ${selected ? '' : 'text-gray-400 dark:text-neutral-500'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className="w-3 h-3 shrink-0 text-gray-400 dark:text-neutral-500"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M3 4.5l3 3 3-3" />
        </svg>
      </button>

      {open && pos &&
        createPortal(
          <div
            ref={popupRef}
            style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
            className="z-[70] flex flex-col bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg shadow-lg overflow-hidden"
            // The popup portals to <body> but React still bubbles its events
            // through the Combobox's React tree — without these stops, a click
            // in the popup reaches the host modal's close-on-overlay-click.
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <input
              ref={inputRef}
              type="text"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setHighlight(0);
              }}
              onKeyDown={onInputKeyDown}
              placeholder="Type to filter…"
              role="searchbox"
              aria-controls={listboxId}
              aria-activedescendant={rowCount > 0 ? `${id}-row-${Math.min(highlight, rowCount - 1)}` : undefined}
              className="w-full px-2.5 py-1.5 text-sm border-b border-gray-200 dark:border-neutral-800 bg-transparent focus:outline-none"
            />
            <ul id={listboxId} role="listbox" className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <li className="px-2.5 py-1.5 text-sm text-gray-400 dark:text-neutral-500">
                  {options.length === 0 ? 'No options' : 'No matches'}
                </li>
              )}
              {filtered.map((opt, i) => {
                const isSelected = opt.value === value;
                const isHighlighted = i === highlight;
                return (
                  <li
                    key={opt.value}
                    id={`${id}-row-${i}`}
                    role="option"
                    aria-selected={isSelected}
                    // Select on mousedown (with preventDefault) so the filter
                    // input doesn't blur before the click lands.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectRow(i);
                    }}
                    onMouseEnter={() => setHighlight(i)}
                    className={`px-2.5 py-1.5 text-sm cursor-pointer ${
                      isHighlighted ? 'bg-gray-100 dark:bg-neutral-800' : ''
                    } ${isSelected ? 'font-medium' : ''}`}
                  >
                    {opt.label}
                  </li>
                );
              })}
            </ul>
            {actions.length > 0 && (
              <div className="border-t border-gray-200 dark:border-neutral-800 py-1">
                {actions.map((action, ai) => {
                  const i = filtered.length + ai;
                  const isHighlighted = i === highlight;
                  return (
                    <div
                      key={action.label}
                      id={`${id}-row-${i}`}
                      role="option"
                      aria-selected={false}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectRow(i);
                      }}
                      onMouseEnter={() => setHighlight(i)}
                      className={`px-2.5 py-1.5 text-sm cursor-pointer ${
                        isHighlighted ? 'bg-gray-100 dark:bg-neutral-800' : ''
                      }`}
                    >
                      {action.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
}
